import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getUserInfo } from '../utils/tokenManager';
import { getHotelBasicInfo } from '../services/productService';
import { getHotelRoomList, getSupplyContractList, createSupplyContract, updateSupplyContract, deleteSupplyContract } from '../services/apiService';
import UnifiedPriceCalendar from '../components/UnifiedPriceCalendar';

/**
 * 호텔 상품 가격/재고 관리 화면
 * 호텔별 객실 타입과 상품의 가격 및 재고를 캘린더 형식으로 관리
 */
const HotelPriceStockSettings = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [error, setError] = useState(null);
  const [roomTypes, setRoomTypes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [supplyContracts, setSupplyContracts] = useState([]);
  const [activeContract, setActiveContract] = useState(null);
  // 통합 캘린더 사용으로 탭 제거
  const [isAddContractModalOpen, setIsAddContractModalOpen] = useState(false);
  const [newContractName, setNewContractName] = useState('');
  const [isCreatingContract, setIsCreatingContract] = useState(false);
  const [isEditContractModalOpen, setIsEditContractModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editContractName, setEditContractName] = useState('');
  const [isUpdatingContract, setIsUpdatingContract] = useState(false);
  // 헤더에서 노출할 객실 타입/상품 선택 상태
  const [headerRoomTypes, setHeaderRoomTypes] = useState([]); // UnifiedPriceCalendar에서 전달받을 원본 구조
  const [selectedRoomType, setSelectedRoomType] = useState('');
  const [selectedRoomProduct, setSelectedRoomProduct] = useState('');
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    const loadHotelInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { companyNo } = getUserInfo() || {};
        if (!companyNo || !productId) {
          setError('필수 정보가 없습니다.');
          return;
        }

        const info = await getHotelBasicInfo(productId);
        if (!info || !info.상품번호) {
          setError('호텔 정보를 찾을 수 없습니다.');
          return;
        }

        setHotelInfo(info);

        // 공급계약 리스트 로드
        try {
          let contracts = await getSupplyContractList({ productNo: productId });
          const hasDefault = Array.isArray(contracts) && contracts.some(c => c.공급계약명 === '기본 공급계약');

          // 공급계약이 없고 기본 공급계약도 없으면 기본 공급계약 자동 생성
          if ((!contracts || contracts.length === 0) && !hasDefault) {
await createSupplyContract({ 
              productNo: productId, 
              contractName: '기본 공급계약' 
            });
            // 다시 로드
            contracts = await getSupplyContractList({ productNo: productId });
          }
          
          setSupplyContracts(contracts);
          if (contracts.length > 0) {
            setActiveContract(contracts[0].공급계약번호);
          }
        } catch (contractErr) {
          console.error('공급계약 정보 로드 실패:', contractErr);
        }

        // 객실 타입 및 상품 로드
        try {
          const roomData = await getHotelRoomList({ 
            productNo: productId, 
            companyNo: String(companyNo) 
          });
          
          if (roomData?.table) {
            const parsedRooms = roomData.table.map(row => {
              try {
                const info = JSON.parse(row.정보 || '{}');
                return {
                  id: row.자원번호,
                  객실명: info.객실명 || row.자원명 || '',
                  products: Array.isArray(info.객실상품) ? info.객실상품.map((p, idx) => ({
                    id: `${row.자원번호}-${idx}`,
                    상품명: p.상품명 || '',
                    확정방식: p.확정방식 || '',
                    환불가능여부: p.환불가능여부 || '',
                  })) : []
                };
              } catch (e) {
                return {
                  id: row.자원번호,
                  객실명: row.자원명 || '',
                  products: []
                };
              }
            });
            setRoomTypes(parsedRooms);
          }
        } catch (roomErr) {
          console.error('객실 정보 로드 실패:', roomErr);
        }
      } catch (err) {
        console.error('호텔 정보 로드 실패:', err);
        setError('호텔 정보를 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    loadHotelInfo();
  }, [productId]);

  // 공급계약 추가 핸들러
  const handleAddContract = async () => {
    if (!newContractName.trim()) {
      alert('공급계약명을 입력해주세요.');
      return;
    }

    try {
      setIsCreatingContract(true);
      await createSupplyContract({ 
        productNo: productId, 
        contractName: newContractName.trim() 
      });
      
      // 공급계약 리스트 다시 로드
      const contracts = await getSupplyContractList({ productNo: productId });
      setSupplyContracts(contracts);
      
      // 새로 생성된 계약 선택 (마지막 항목)
      if (contracts.length > 0) {
        setActiveContract(contracts[contracts.length - 1].공급계약번호);
      }
      
      // 모달 닫기 및 초기화
      setIsAddContractModalOpen(false);
      setNewContractName('');
      alert('공급계약이 추가되었습니다.');
    } catch (error) {
      console.error('공급계약 추가 실패:', error);
      alert('공급계약 추가에 실패했습니다.');
    } finally {
      setIsCreatingContract(false);
    }
  };

  // 공급계약 편집 모달 열기
  const handleOpenEditModal = (contract) => {
    setEditingContract(contract);
    setEditContractName(contract.공급계약명);
    setIsEditContractModalOpen(true);
  };

  // 공급계약 편집 핸들러
  const handleEditContract = async () => {
    if (!editContractName.trim()) {
      alert('공급계약명을 입력해주세요.');
      return;
    }

    try {
      setIsUpdatingContract(true);
      await updateSupplyContract({ 
        productNo: productId, 
        contractNo: editingContract.공급계약번호,
        contractName: editContractName.trim() 
      });
      
      // 공급계약 리스트 다시 로드
      const contracts = await getSupplyContractList({ productNo: productId });
      setSupplyContracts(contracts);
      
      // 모달 닫기 및 초기화
      setIsEditContractModalOpen(false);
      setEditingContract(null);
      setEditContractName('');
      alert('공급계약이 수정되었습니다.');
    } catch (error) {
      console.error('공급계약 수정 실패:', error);
      alert('공급계약 수정에 실패했습니다.');
    } finally {
      setIsUpdatingContract(false);
    }
  };

  // 공급계약 삭제 핸들러
  const handleDeleteContract = async (contract) => {
    if (!confirm(`"${contract.공급계약명}" 공급계약을 삭제하시겠습니까?\n\n삭제된 공급계약의 가격/재고 데이터도 함께 삭제될 수 있습니다.`)) {
      return;
    }

    try {
      await deleteSupplyContract({ 
        productNo: productId, 
        contractNo: contract.공급계약번호
      });
      
      // 공급계약 리스트 다시 로드
      const contracts = await getSupplyContractList({ productNo: productId });
      setSupplyContracts(contracts);
      
      // 삭제된 계약이 현재 선택된 계약이면 첫 번째 계약으로 변경
      if (activeContract === contract.공급계약번호) {
        if (contracts.length > 0) {
          setActiveContract(contracts[0].공급계약번호);
        } else {
          setActiveContract(null);
        }
      }
      
      alert('공급계약이 삭제되었습니다.');
    } catch (error) {
      console.error('공급계약 삭제 실패:', error);
      alert('공급계약 삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">호텔 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl px-0 sm:px-0 lg:px-0 w-full">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="뒤로 가기"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {hotelInfo?.상품명 || '호텔 상품'}
                </h1>
                <p className="text-sm text-gray-500">가격/재고 관리</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/detail-settings/${productId}`}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                상세 설정으로
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-full px-2 sm:px-3 lg:px-2 py-6 flex-1 min-h-0 flex flex-col">
        {/* Compact Header: Supply Contract + Room Selectors */}
        <div className="bg-white rounded-lg shadow-sm border mb-4 px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Room Type / Product selectors (inline with dropdowns) */}
            <div className="flex items-center gap-3 min-w-0">
              {(headerRoomTypes && headerRoomTypes.length > 0) ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 whitespace-nowrap">객실 타입</span>
                    <select
                      className="px-2 py-1.5 text-xs border rounded-md max-w-[220px]"
                      value={selectedRoomType}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedRoomType(val);
                        const rt = headerRoomTypes.find(r => r.객실타입 === val);
                        const first = rt?.객실상품?.[0]?.객실상품명;
                        if (first) setSelectedRoomProduct(first);
                        else setSelectedRoomProduct('');
                      }}
                    >
                      {headerRoomTypes.map((rt) => (
                        <option key={rt.객실타입번호} value={rt.객실타입}>{rt.객실타입}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 whitespace-nowrap">객실 상품</span>
                    <select
                      className="px-2 py-1.5 text-xs border rounded-md max-w-[260px]"
                      value={selectedRoomProduct}
                      onChange={(e) => setSelectedRoomProduct(e.target.value)}
                      disabled={!selectedRoomType}
                    >
                      {headerRoomTypes
                        .find(r => r.객실타입 === selectedRoomType)?.객실상품
                        ?.map((p, idx) => (
                          <option key={`${selectedRoomType}-${idx}`} value={p.객실상품명}>{p.객실상품명}</option>
                        ))}
                    </select>
                  </div>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate(`/detail-settings/${productId}`)}
                  className="px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                >
                  객실 및 객실 상품을 먼저 등록하세요
                </button>
              )}
            </div>

            {/* Right: Supply Contract Selection */}
            {supplyContracts.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600 font-medium">공급계약:</span>
                <div className="flex flex-wrap gap-1.5">
                  {supplyContracts.map((contract) => (
                    <div key={contract.공급계약번호} className="relative group">
                      <button
                        onClick={() => setActiveContract(contract.공급계약번호)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          activeContract === contract.공급계약번호
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {contract.공급계약명}
                      </button>
                      <div className="absolute top-0 right-0 -mt-1 -mr-1 hidden group-hover:flex gap-0.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(contract);
                          }}
                          className="p-0.5 bg-white border border-gray-300 rounded shadow-sm hover:bg-gray-50 transition-colors"
                          title="편집"
                        >
                          <svg className="w-2.5 h-2.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteContract(contract);
                          }}
                          className="p-0.5 bg-white border border-red-300 rounded shadow-sm hover:bg-red-50 transition-colors"
                          title="삭제"
                        >
                          <svg className="w-2.5 h-2.5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => setIsAddContractModalOpen(true)}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                    title="공급계약 추가"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    추가
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Unified Calendar Content */}
        {activeContract ? (
          <div className="flex-1 min-h-0 flex flex-col">
            <UnifiedPriceCalendar
              roomTypes={roomTypes}
              supplyContractNo={activeContract}
              hideInternalSelectors={true}
              selectedRoomType={selectedRoomType}
              selectedRoomProduct={selectedRoomProduct}
              onSelectedRoomTypeChange={(val) => setSelectedRoomType(val)}
              onSelectedRoomProductChange={(val) => setSelectedRoomProduct(val)}
              onRoomListLoaded={(list) => {
                setHeaderRoomTypes(list || []);
                // 초기값 보정 (없을 때만)
                if ((!selectedRoomType || !selectedRoomProduct) && list && list.length > 0) {
                  const defType = list[0].객실타입;
                  const defProd = list[0].객실상품?.[0]?.객실상품명 || '';
                  if (!selectedRoomType) setSelectedRoomType(defType);
                  if (!selectedRoomProduct && defProd) setSelectedRoomProduct(defProd);
                }
              }}
              onSave={(data) => {
// TODO: API 호출하여 저장
              }}
            />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="text-center py-12">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">공급계약이 없습니다</h3>
              <p className="mt-1 text-sm text-gray-500">가격/재고를 관리하려면 먼저 공급계약을 생성해주세요.</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Contract Modal */}
      {isAddContractModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">공급계약 추가</h3>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                공급계약명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newContractName}
                onChange={(e) => setNewContractName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isCreatingContract) {
                    handleAddContract();
                  }
                }}
                placeholder="예: 여름 시즌 계약"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isCreatingContract}
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsAddContractModalOpen(false);
                  setNewContractName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isCreatingContract}
              >
                취소
              </button>
              <button
                onClick={handleAddContract}
                disabled={isCreatingContract || !newContractName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreatingContract ? '추가 중...' : '추가'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Contract Modal */}
      {isEditContractModalOpen && editingContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">공급계약 편집</h3>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                공급계약명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editContractName}
                onChange={(e) => setEditContractName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isUpdatingContract) {
                    handleEditContract();
                  }
                }}
                placeholder="예: 여름 시즌 계약"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isUpdatingContract}
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button
                onClick={() => {
                  setIsEditContractModalOpen(false);
                  setEditingContract(null);
                  setEditContractName('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isUpdatingContract}
              >
                취소
              </button>
              <button
                onClick={handleEditContract}
                disabled={isUpdatingContract || !editContractName.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingContract ? '수정 중...' : '수정'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelPriceStockSettings;
